/**
 * A tela de entrada — um botão que leva ao Keycloak.
 *
 * **O que havia aqui era código vivo chamando um backend que não
 * existe.** Formulário de e-mail e senha, desafio de MFA e botão do
 * Google falavam com `/auth/login`, `/auth/mfa/verify` e `/auth/google`
 * do backend DESCARTADO — quem tentasse entrar tomava erro de rede. A
 * identidade mora inteira no Keycloak desde 2026-07-30, então senha,
 * segundo fator e conta do Google são assunto de lá, e a tela que os
 * pedia aqui não tinha como funcionar nem em teoria.
 *
 * Sobram dois estados: "entre" e "este ambiente não tem autenticação
 * configurada". O segundo não é decorativo — é o que uma instalação sem
 * `VITE_KEYCLOAK_ISSUER` mostra, e dizer isso em voz alta é melhor que
 * oferecer um botão que não vai a lugar nenhum.
 *
 * Layout (decisão da dona, 2026-10-15): título "Entrar" com a frase,
 * aviso, botão com o "G" do Google, "Entrar com outra conta", "ou",
 * alternativas e rodapé — **sempre no claro**. A tela não segue o modo
 * escuro nem o tema do usuário: as cores ficam fixas em `pages.css`. O
 * pôster do fundo é o mesmo da tela de login do tema `reputaition` do
 * realm, pra onde o clique leva.
 */

import { AlertCircle, LoaderCircle, LogIn, UserRoundPlus } from "lucide-react";

type LoginScreenProps = {
  loading: boolean;
  error: string | null;
  /** Ausente quando o Keycloak não está configurado neste ambiente. */
  onLogin: (() => void) | null;
  /** O login por pessoa está ligado (`"google"`): o botão principal leva
   *  ao Google, e `onLoginWithPassword` é a porta das exceções. */
  loginProvider?: string | null;
  onLoginWithPassword?: (() => void) | null;
  /** "Entrar com outra conta": encerra a sessão no Keycloak e volta com a
   *  escolha de conta do Google (2026-10-15). Sempre um botão logo abaixo
   *  do principal (decisão da dona: o layout é um só, com ou sem erro). */
  onSwitchAccount?: (() => void) | null;
};

/** O "G" do Google, nas cores da marca — o reconhecimento do botão vem
 *  dele, não do texto. */
function GoogleMark() {
  return (
    <span className="login-google-mark" aria-hidden="true">
      <svg viewBox="0 0 48 48" width="18" height="18">
        <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
        <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
        <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
        <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
      </svg>
    </span>
  );
}

export function LoginScreen({
  loading,
  error,
  onLogin,
  loginProvider = null,
  onLoginWithPassword = null,
  onSwitchAccount = null
}: LoginScreenProps) {
  const viaGoogle = loginProvider === "google";
  const passwordDoor = viaGoogle ? onLoginWithPassword : null;

  return (
    <main className="login-page">
      <video
        className="login-page-video"
        src="/login/wave-v2.mp4"
        poster="/login/wave-poster.jpg"
        autoPlay
        loop
        muted
        playsInline
        aria-hidden="true"
        tabIndex={-1}
      />

      <section className="login-book" aria-labelledby="login-title">
        <img src="/brand/reputation-complete.svg" alt="ReputAItion" className="login-logo-img"/>

        {onLogin ? (
          <>
            <header className="login-head">
              <h1 id="login-title">Entrar</h1>
              <p>
                {viaGoogle
                  ? "Use a sua conta Google da empresa para acessar o ReputAItion."
                  : "Você vai entrar com sua conta FSB. Senha, verificação em duas etapas e sessões ativas são gerenciadas por lá."}
              </p>
            </header>

            {error && (
              <div className="login-alert" role="alert">
                <AlertCircle size={17} aria-hidden="true"/>
                <span>{error}</span>
              </div>
            )}

            <div className="login-actions">
              <button
                type="button"
                className="login-primary"
                onClick={onLogin}
                disabled={loading}
              >
                {loading
                  ? <><LoaderCircle className="spin" size={17}/>Redirecionando…</>
                  : viaGoogle
                    ? <><GoogleMark/>Entrar com a conta Google</>
                    : <><LogIn size={17}/>Entrar com a conta FSB</>}
              </button>
              {onSwitchAccount && (
                <button
                  type="button"
                  className="login-secondary"
                  onClick={onSwitchAccount}
                  disabled={loading}
                >
                  <UserRoundPlus size={16} aria-hidden="true"/>
                  Entrar com outra conta
                </button>
              )}
            </div>

            {passwordDoor && (
              <>
                <div className="login-divider">ou</div>
                <div className="login-alternatives">
                  {passwordDoor && (
                    <button type="button" className="login-link" onClick={passwordDoor} disabled={loading}>
                      Não tenho conta Google da empresa — entrar com usuário e senha
                    </button>
                  )}
                </div>
              </>
            )}

            <p className="login-foot">Problemas para entrar? Fale com um administrador do ReputAItion.</p>
          </>
        ) : (
          <>
            <header className="login-head">
              <h1 id="login-title">Autenticação indisponível</h1>
              <p>
                Este ambiente não tem um serviço de autenticação configurado,
                então não há como entrar. Defina <code>VITE_KEYCLOAK_ISSUER</code>{" "}
                e <code>VITE_KEYCLOAK_CLIENT_ID</code>, ou use{" "}
                <code>VITE_DEV_USER</code> em desenvolvimento.
              </p>
            </header>
            {error && (
              <div className="login-alert" role="alert">
                <AlertCircle size={17} aria-hidden="true"/>
                <span>{error}</span>
              </div>
            )}
          </>
        )}
      </section>
    </main>
  );
}
